import React, { useState, useEffect } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Box,
} from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import api from "../../services/api";
import toastError from "../../errors/toastError";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  btnWrapper: {
    position: "relative",
  },
  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
  helpText: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius,
  },
}));

const TelegramConnectionSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Muy corto")
    .max(50, "Muy largo")
    .required("Requerido"),
  botToken: Yup.string()
    .matches(
      /^\d+:[A-Za-z0-9_-]{35}$/,
      "Formato de Bot Token inválido"
    )
    .required("Requerido"),
  greetingMessage: Yup.string(),
  farewellMessage: Yup.string(),
});

const TelegramConnectionModal = ({ open, onClose, connectionId }) => {
  const classes = useStyles();
  const [showToken, setShowToken] = useState(false);

  const initialState = {
    name: "",
    botToken: "",
    greetingMessage: "",
    farewellMessage: "",
  };

  const [connection, setConnection] = useState(initialState);

  useEffect(() => {
    const fetchConnection = async () => {
      // No intentar cargar si el modal no está abierto o no hay connectionId
      if (!open || !connectionId) return;
      
      try {
        const { data } = await api.get(`/telegram/connections/${connectionId}`);
        setConnection(data);
      } catch (err) {
        // Solo mostrar error si no es un error de "conexión no encontrada"
        // (que ocurre cuando se intenta abrir el modal de Telegram para una conexión de WhatsApp)
        const errorMessage = err?.response?.data?.error || "";
        if (errorMessage !== "ERR_TELEGRAM_CONNECTION_NOT_FOUND") {
          toastError(err);
        }
        // Cerrar el modal silenciosamente
        onClose();
      }
    };
    fetchConnection();
  }, [open, connectionId, onClose]);

  const handleClose = () => {
    onClose();
    setConnection(initialState);
  };

  const handleSaveConnection = async (values) => {
    const connectionData = {
      ...values,
      channel: "telegram",
    };

    try {
      if (connectionId) {
        await api.put(`/telegram/connections/${connectionId}`, connectionData);
        toast.success("Bot de Telegram actualizado exitosamente");
      } else {
        await api.post("/telegram/connections", connectionData);
        toast.success("Bot de Telegram conectado exitosamente");
      }
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {connectionId
            ? "Editar Bot de Telegram"
            : "Agregar Bot de Telegram"}
        </DialogTitle>
        <Formik
          initialValues={connection}
          enableReinitialize={true}
          validationSchema={TelegramConnectionSchema}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveConnection(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ touched, errors, isSubmitting }) => (
            <Form>
              <DialogContent dividers>
                <Box className={classes.helpText}>
                  <Typography variant="body2" color="textSecondary">
                    Cómo crear un bot de Telegram:
                  </Typography>
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                    1. Abre Telegram y busca @BotFather
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    2. Envía el comando /newbot y sigue las instrucciones
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    3. Copia el Bot Token que te proporciona @BotFather
                  </Typography>
                </Box>

                <Field
                  as={TextField}
                  label="Nombre del Bot"
                  autoFocus
                  name="name"
                  error={touched.name && Boolean(errors.name)}
                  helperText={touched.name && errors.name}
                  variant="outlined"
                  margin="dense"
                  fullWidth
                />

                <Field
                  as={TextField}
                  label="Bot Token"
                  name="botToken"
                  type={showToken ? "text" : "password"}
                  error={touched.botToken && Boolean(errors.botToken)}
                  helperText={
                    touched.botToken && errors.botToken
                      ? errors.botToken
                      : "Formato: 123456789:ABCdefGHIjklMNOpqrsTUVwxyz"
                  }
                  variant="outlined"
                  margin="dense"
                  fullWidth
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowToken(!showToken)}
                          edge="end"
                        >
                          {showToken ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Field
                  as={TextField}
                  label="Mensaje de Bienvenida"
                  name="greetingMessage"
                  error={touched.greetingMessage && Boolean(errors.greetingMessage)}
                  helperText={
                    touched.greetingMessage && errors.greetingMessage
                      ? errors.greetingMessage
                      : "Mensaje enviado cuando el usuario inicia conversación con /start"
                  }
                  variant="outlined"
                  margin="dense"
                  multiline
                  rows={3}
                  fullWidth
                />

                <Field
                  as={TextField}
                  label="Mensaje de Despedida"
                  name="farewellMessage"
                  error={touched.farewellMessage && Boolean(errors.farewellMessage)}
                  helperText={
                    touched.farewellMessage && errors.farewellMessage
                      ? errors.farewellMessage
                      : "Mensaje enviado cuando se cierra el ticket"
                  }
                  variant="outlined"
                  margin="dense"
                  multiline
                  rows={3}
                  fullWidth
                />
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  Cancelar
                </Button>
                <div className={classes.btnWrapper}>
                  <Button
                    type="submit"
                    color="primary"
                    disabled={isSubmitting}
                    variant="contained"
                  >
                    {connectionId ? "Guardar" : "Agregar"}
                  </Button>
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </div>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default TelegramConnectionModal;
